import { type FC, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { Alert } from '@vakantiesnl/components/src/atoms/Alert';
import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { Link } from '@vakantiesnl/components/src/atoms/Link/Link';
import { Tag } from '@vakantiesnl/components/src/atoms/Tag';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { StepTitle } from '@vakantiesnl/components/src/blocks/accommodation/StepTitle/StepTitle';
import { getExtraPriceTitle } from '@vakantiesnl/components/src/molecules/FlightSelector/helpers';
import { BaseSingleSelect } from '@vakantiesnl/components/src/molecules/SelectBar/SingleSelect/BaseSingleSelect';
import { message } from '@vakantiesnl/components/src/utils';
import { useAccoAvailabilityCheck } from '@vakantiesnl/components/src/utils/accommodation/useAccoAvailabilityCheck';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import ImageComponent from '@vakantiesnl/image-component';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { useTripFilterContext } from '@vakantiesnl/services/src/context/useTripFilterContext';
import { useGetAccommodationByEntityId, useGetAccommodationOnlyOffers } from '@vakantiesnl/services/src/hooks/queries';
import { useAccoOfferStore } from '@vakantiesnl/services/src/stores/accommodationStore';
import { type Search } from '@vakantiesnl/types';
import dynamic from 'next/dynamic';

import { useStyles } from './RoomType.styles';

const CompassLoader = dynamic(
	() => import('@vakantiesnl/components/src/atoms/CompassLoader').then((mod) => ({ default: mod.CompassLoader })),
	{ ssr: false },
);

const RoomInfoModal = dynamic(
	() => import('@vakantiesnl/components/src/molecules/RoomInfoModal').then((mod) => ({ default: mod.RoomInfoModal })),
	{ ssr: false },
);

type RoomAttributeState = { attributes?: string[]; roomTitle?: string; images: { url: string }[] | null };

const DEFAULT_AMOUNT_OF_ROOMTYPE = 5;
const ROOMTYPE_PAGE_SIZE = 5;

const RoomType: FC = () => {
	const { isDesktop, isMobile } = useBreakpoints();
	const { classes } = useStyles();
	const microCopies = useMicroCopyContext();

	const [modalAttributesContent, setModalAttributesContent] = useState<RoomAttributeState | undefined>();
	const [showModal, setShowModal] = useState(false);

	const selectedOffer = useAccoOfferStore((s) => s.accoOnlyRoomTypeOffer);
	const selectedDepartureDateOffer = useAccoOfferStore((s) => s.accoOnlyDepartureDateOffer);
	const setAccoOnlyAccoOffer = useAccoOfferStore((s) => s.setAccoOnlyRoomTypeOffer);
	const isLoadingCheapestOfferPerDay = useAccoOfferStore((s) => s.isDepartureDateLoading);

	const {
		state: tripFiltersState,
		dispatch: { updateSelectedRoomType },
	} = useTripFilterContext();
	const { accommodation } = useGetAccommodationByEntityId();
	const { accoOnlyOffers, isLoadingAccoOnlyOffers } = useGetAccommodationOnlyOffers();

	const trackCTAClick = useCallback(() => {
		import('@vakantiesnl/services/src/gtm/common').then(({ track }) => {
			import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ bookNowRoomType }) =>
				track(bookNowRoomType(accommodation)),
			);
		});
	}, [accommodation]);

	const { checkAvailability } = useAccoAvailabilityCheck({ onClick: trackCTAClick, gtmLabel: 'room_type' });

	/**
	 * Get the index +1 when a selected roomtype is in the URL;
	 * Used to set the pagination for the roomtypes open up until the selected one
	 */
	const initialItemsToShow = useMemo(() => {
		if (accoOnlyOffers && tripFiltersState.selected.roomType) {
			const roomTypeIndex = accoOnlyOffers.offers.findIndex(
				(offer) => String(offer.productCode) === tripFiltersState.selected.roomType,
			);
			if (roomTypeIndex === -1 || roomTypeIndex < 5) return DEFAULT_AMOUNT_OF_ROOMTYPE;
			return roomTypeIndex + 1;
		}
		return DEFAULT_AMOUNT_OF_ROOMTYPE;
	}, [accoOnlyOffers, tripFiltersState.selected.roomType]);

	const [itemsToShow, setItemsToShow] = useState(initialItemsToShow);

	// This object holds the possible offers for each room type at each room position
	const roomTypeOffers = useMemo(() => {
		if (!accoOnlyOffers?.offers) return [];

		return accoOnlyOffers.offers;
	}, [accoOnlyOffers]);

	const toggleRoomInfoModal = useCallback(
		(offer: Search.AccommodationOnlyOffer) => () => {
			if (offer.productInfo) {
				setShowModal(!showModal);
				setModalAttributesContent({
					images: offer.productInfo.images
						? offer.productInfo.images.filter(({ is_thumb }) => !is_thumb)
						: null,
					attributes: offer.productInfo.properties
						.filter((property) => property.descriptions.length > 0)
						.map((property) => {
							if (property.unitTag === 'def_Count')
								return `${property.descriptions?.[0].description}: ${property.quantity}`;
							return property.descriptions?.[0].description;
						}),
					roomTitle: offer.productInfo.name,
				});
			}
			if (!showModal) {
				import('@vakantiesnl/services/src/gtm/common').then(({ track }) => {
					import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ onRoomTypeOpen }) =>
						track(onRoomTypeOpen(accommodation)),
					);
				});
			}
		},
		[showModal, accommodation],
	);

	const onRoomTypeSelect = useCallback(
		(offer: Search.AccommodationOnlyOffer, priceDifference: number | undefined, roomType: string) => (): void => {
			setAccoOnlyAccoOffer(offer);

			/** Set the room type for selectable offer URL */
			updateSelectedRoomType(offer.productCode.toString());

			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ roomSelect }) => {
					track(roomSelect(accommodation, offer, roomType, priceDifference));
				}),
			);
		},
		[accommodation, setAccoOnlyAccoOffer, updateSelectedRoomType],
	);

	const onCheckAvailability = useCallback(
		(offer: Search.AccommodationOnlyOffer, priceDifference: number | undefined, roomType: string) => () => {
			onRoomTypeSelect(offer, priceDifference, roomType)();
			checkAvailability(offer);
		},
		[checkAvailability, onRoomTypeSelect],
	);

	const getMaxOccupancyText = useCallback(
		(occupancy: string) => message(microCopies['accommodation.room.maxOccupancy'], { maxOccupancy: occupancy }),
		[microCopies],
	);

	const closeModal = useCallback(() => setShowModal(false), []);

	const renderParagraph = useCallback(
		(offer: Search.AccommodationOnlyOffer): ReactNode => {
			if (offer.productInfo) {
				const maxOccupancy = getMaxOccupancyText(String(offer.productInfo.maxOccupancy));

				return (
					<>
						{maxOccupancy}
						{offer.productInfo.properties && offer.productInfo.properties.length > 0 && (
							<>
								{' • '}
								<Link onClick={toggleRoomInfoModal(offer)} data-cy="room-type-more-info-button">
									{microCopies['moreInfo']}
								</Link>
							</>
						)}
					</>
				);
			}
			return null;
		},

		[getMaxOccupancyText, microCopies, toggleRoomInfoModal],
	);

	/** Set the current selected offer on change of accoOnlyOffers */
	/** Set the initial items to show on change of selected price bar and mounting */
	useEffect(() => {
		if (!!selectedOffer) {
			setItemsToShow(initialItemsToShow);
		}
	}, [initialItemsToShow, selectedOffer]);

	const handleShowMoreRoomType = useCallback(() => {
		/**
		 * @if the items to show + page size is bigger then the amount of total rooms; set the room size as page size
		 * @else increase the items to show with the page size
		 */
		if (itemsToShow + ROOMTYPE_PAGE_SIZE > roomTypeOffers.length) setItemsToShow(roomTypeOffers.length);
		else setItemsToShow((itemsToShow) => itemsToShow + ROOMTYPE_PAGE_SIZE);
	}, [roomTypeOffers.length, itemsToShow]);

	/** Don't render anything when there're no selected offer in the price bar */
	if (!Boolean(selectedDepartureDateOffer) || isLoadingCheapestOfferPerDay) return null;

	/** Show loading indicator */
	if (isLoadingAccoOnlyOffers) {
		return (
			<div data-cy="room-type-selector">
				<div className={classes.titleWrapper}>
					<StepTitle title={microCopies['accommodation.acco_type.title']} />
				</div>
				<CompassLoader />
			</div>
		);
	}

	/** Show alert when there's a selected offer and room offers available and they're not loading */
	if (roomTypeOffers === null || !roomTypeOffers || (Array.isArray(roomTypeOffers) && roomTypeOffers.length === 0)) {
		return (
			<div data-cy="room-type-selector">
				<div className={classes.titleWrapper}>
					<StepTitle title={microCopies['accommodation.acco_type.title']} />
				</div>
				<Alert variant="warning" text={microCopies['roomfilters.noresults']} />
			</div>
		);
	}

	return (
		<>
			<div data-cy="room-type-selector">
				<div className={classes.titleWrapper}>
					<StepTitle title={microCopies['accommodation.acco_type.title']} />
				</div>
				<div className={classes.roomsContainer}>
					{roomTypeOffers.slice(0, itemsToShow).map((offer, key) => {
						if (!offer.productInfo) return;

						const thumbImage = offer.productInfo.images.find((i) => i.is_thumb);

						/** Only show price differences when the offer package price is not equal to the cheapest offer */
						const priceDifference =
							accoOnlyOffers?.cheapestOffer &&
							offer.pricePackage - accoOnlyOffers?.cheapestOffer.pricePackage > 0
								? offer.pricePackage - accoOnlyOffers?.cheapestOffer.pricePackage
								: undefined;

						return (
							<BaseSingleSelect
								key={`roomType${key}`}
								selected={selectedOffer?.id === offer.id}
								id={`roomType${offer.id}`}
								value={offer.id}
								name={`roomType${offer.id}`}
								tag={
									<div className={classes.tagWrapper}>
										<Tag
											color="orange"
											variant={priceDifference ? 'outline' : 'filled'}
											size="small"
										>
											{priceDifference
												? getExtraPriceTitle(priceDifference)
												: microCopies['cheapestOption']}
										</Tag>
										{!isMobile && (
											<Button
												variant="primary"
												size={'small'}
												onClick={onCheckAvailability(
													offer,
													priceDifference,
													offer.productInfo.name,
												)}
											>
												{microCopies['accommodation.room.bookAccoOnly']}
											</Button>
										)}
									</div>
								}
								dataCy="roomTypeOption"
								onSelect={onRoomTypeSelect(offer, priceDifference, offer.productInfo.name)}
								content={
									<div className={classes.roomsContent}>
										{thumbImage?.url && (
											<ImageComponent
												alt={thumbImage.url}
												src={thumbImage.url}
												rotationDegrees={0}
												width={248}
												height={176}
												className={classes.roomImage}
											/>
										)}
										<div>
											<Typography variant={isDesktop ? 'labelMd' : 'labelSm'} as="span">
												{offer.productInfo.name}
											</Typography>
											<Typography variant="bodySm" as="p" data-cy="roomTypeOption-paragraph">
												{renderParagraph(offer)}
											</Typography>
										</div>
										{isMobile && (
											<Button
												variant="primary"
												size={'small'}
												onClick={onCheckAvailability(
													offer,
													priceDifference,
													offer.productInfo.name,
												)}
											>
												{microCopies['accommodation.room.bookAccoOnly']}
											</Button>
										)}
									</div>
								}
							/>
						);
					})}
				</div>

				{roomTypeOffers.length > ROOMTYPE_PAGE_SIZE && roomTypeOffers.length > itemsToShow && (
					<div className={classes.showMoreContainer}>
						<Button
							variant="secondary"
							onClick={handleShowMoreRoomType}
							data-cy="roomType-pagination"
							size={isDesktop ? 'medium' : 'small'}
						>
							{microCopies['filters.showMore']}
						</Button>
					</div>
				)}
			</div>
			{modalAttributesContent?.attributes && modalAttributesContent?.roomTitle && (
				<RoomInfoModal
					title={modalAttributesContent.roomTitle}
					attributes={modalAttributesContent.attributes}
					onClose={closeModal}
					images={modalAttributesContent.images}
					open={showModal}
				/>
			)}
		</>
	);
};

export default RoomType;
